// Click-to-enlarge for the product screenshots on /platform/.
//
// Enhancement, like everything else on that page. Each screenshot ships wrapped in a real
// <a> pointing at its own 2x file, so with no JS a click opens the full-size image on its
// own — which is what the control promises. This module intercepts that click and shows
// the image in a dialog instead. The link is never a dead control waiting for a bundle.
//
// One dialog for all of them. The only thing that varies between 26 screenshots is the
// src and the alt, and 26 dialogs would be 26 copies of the same markup in the DOM.
//
// Built on native <dialog>.showModal(), which is doing more work here than it looks:
// Esc to close, the focus trap, the backdrop, and `inert` on the rest of the page are all
// the platform's rather than ours. Reimplementing any of that by hand is how lightboxes
// end up trapping keyboard users.

import lenis from "../managers/lenis.js";

const dialog = document.querySelector("[data-lightbox-dialog]");
const image = dialog?.querySelector("[data-lightbox-image]");

if (dialog && image && typeof dialog.showModal === "function") {
    // The link that was clicked, so focus can be put back where it came from. The spec
    // says a dialog restores focus to the previously-focused element on close, but that
    // has been uneven across browsers, and a visitor who tabs from the top of the page
    // after closing has lost their place — cheap enough to do explicitly.
    let opener = null;

    document.querySelectorAll("[data-lightbox]").forEach((link) => {
        link.addEventListener("click", (event) => {
            // Let a modified click do what the visitor asked — open in a new tab, save
            // the file, and so on. Only a plain left click becomes a dialog.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

            event.preventDefault();
            opener = link;

            // Straight from the <img> rather than the href, so the enlarged image and the
            // thumbnail can never describe different things.
            const thumb = link.querySelector("img");
            image.src = link.getAttribute("href");
            image.alt = thumb ? thumb.alt : "";

            // showModal() makes the rest of the page inert but does not stop it
            // scrolling, and Lenis handles the wheel at window level, so without this the
            // page runs on behind the open dialog — measured at scrollY 338 -> 1387 over
            // five wheel ticks — and `close` then returns focus to an opener that is no
            // longer on screen. modules/nav.js locks its overlay with the same pair;
            // stopping Lenis flips `lenis-stopped` on <html>, which lenis.css turns into
            // `overflow: clip` on the root.
            lenis.stop();
            dialog.showModal();
        });
    });

    // No handler for the close button: it is a submit inside <form method="dialog">, so
    // the element closes itself and the `close` handler below still runs the cleanup.

    // Clicking the backdrop closes it. The dialog element's own box covers the whole
    // viewport, so "did they click the backdrop" is really "did they click outside the
    // image", which is what this measures.
    dialog.addEventListener("click", (event) => {
        if (event.target !== dialog) return;

        const box = image.getBoundingClientRect();
        const outside = event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;

        if (outside) dialog.close();
    });

    dialog.addEventListener("close", () => {
        // Paired with the stop() on open. On `close` rather than on the close button, so
        // it runs for every route out — Esc, the backdrop, the button, or anything that
        // calls close() later.
        lenis.start();

        // Drop the src so a large screenshot is not held in memory behind a closed
        // dialog, and so the next open cannot flash the previous image before the new one
        // decodes.
        image.removeAttribute("src");
        image.alt = "";

        if (opener) {
            opener.focus();
            opener = null;
        }
    });
}
